import { useField } from "formik";
import React, { forwardRef } from "react";
import { Experience, Payment, PaymentMethod, PaymentReminderType, PurchaseItemObject } from "types";
import shallow from "zustand/shallow";
import { useSeller, useUser } from "../../hooks/useAccount";
import { isCreditCardRequired, showCustomerFields } from "../../models/Experience";
import { isAffiliate } from "../../models/User";
import { useCreatePurchaseStore } from "../../stores/createPurchaseStore";
import { PaymentInput } from "../Input/PaymentInput";
import { PaymentInputRef, TokenizePaymentParams } from "../Input/PaymentInputRef";
import { CreatePurchaseDepositFields } from "./CreatePurchaseDepositFields";
import { CreatePurchaseInvoiceFields } from "./CreatePurchaseInvoiceFields";
import { isExperiencePurchaseItem, isMembershipItem } from "@/models/PurchaseItem";

export interface CreatePurchasePaymentFieldsProps {
    onAdditionalDetailsSubmit: (tokenizePaymentParams: TokenizePaymentParams) => Promise<void>;
    onAdditionalDetailsSubmitSuccess?: () => void;
}

export const CreatePurchasePaymentFields = forwardRef<PaymentInputRef, CreatePurchasePaymentFieldsProps>(
    ({ onAdditionalDetailsSubmit, onAdditionalDetailsSubmitSuccess }: CreatePurchasePaymentFieldsProps, ref) => {
        const user = useUser();
        const helpers = useField("payment")[2];
        const seller = useSeller();
        const organizer = useCreatePurchaseStore((state) => state.organizer);
        const experience = useCreatePurchaseStore((state) => state.getActiveProduct());
        const { setPurchase, setPayment } = useCreatePurchaseStore.getState().actions;

        const [activeItemIndex, isGift, hasMembershipItem, hasExperienceItem] = useCreatePurchaseStore((state) => {
            const activeItem = state.getActiveItem();
            return [
                state.activeItemIndex,
                activeItem.object === PurchaseItemObject.GIFT,
                state.purchase.items.some((purchaseItem) => isMembershipItem(purchaseItem)),
                state.purchase.items.some((purchaseItem) => isExperiencePurchaseItem(purchaseItem)),
            ];
        }, shallow);

        const [purchaseAmount, purchaseDueNow] = useCreatePurchaseStore((state) => {
            return [state.amount, state.dueNow];
        }, shallow);

        // TODO previewPurchase will have partnerSeller, but purchase won't for packages as it's added later and not set in state
        const isPartnerPackage = useCreatePurchaseStore((state) =>
            state.purchase.items.some((item) => item.seller && item.seller.id !== seller.id),
        );

        const isPartnerExperience = experience?.seller.id !== seller.id;
        const isPartnerBooking = isPartnerExperience || isPartnerPackage;

        const depositDurationHelper = useField("depositDuration");

        const [paymentAmount, paymentMethod] = useCreatePurchaseStore(
            (state) => [state.paymentLineItem.amount, state.paymentLineItem.payment.method],
            shallow,
        );

        const shouldCollectCC = purchaseAmount === 0 && isCreditCardRequired(experience as Experience);

        const handlePaymentChange = (payment: Payment) => {
            setPurchase((draft) => {
                const reminders =
                    draft.paymentReminders?.filter((reminder) => depositRemindersTypes.has(reminder.type)) ?? [];
                draft.paymentReminders = [...reminders];
            });
            setPayment(payment);

            const { balanceDueOffset } = useCreatePurchaseStore.getState().purchase.items[activeItemIndex];
            depositDurationHelper[2].setValue(balanceDueOffset ?? 0);
        };

        // All methods except credit card excluded for affiliate / agent bookings including the custom seller methods
        // TODO: Refactor this part
        const customSellerMethods = seller.paymentMethods?.map((paymentMethod) => paymentMethod.id) ?? [];
        const methodsExcludedForAffiliate = [...Object.values(PaymentMethod), ...customSellerMethods].filter(
            (method) => method !== PaymentMethod.CREDIT_CARD,
        );
        const excludePaymentMethods =
            isAffiliate(user) || isPartnerBooking
                ? methodsExcludedForAffiliate
                : isGift || hasMembershipItem
                ? [PaymentMethod.INVOICE]
                : [];

        if (!showCustomerFields(experience as Experience)) {
            excludePaymentMethods.push(PaymentMethod.INVOICE);
        }

        if (!hasExperienceItem) {
            excludePaymentMethods.push(PaymentMethod.INVOICE);
        }

        // Don't show don't charge now switch for partner experience
        const showDoNotChargeCardSwitch = !isPartnerBooking;

        return (
            <div>
                {paymentMethod === PaymentMethod.LATER || (purchaseDueNow === 0 && !shouldCollectCC) ? null : (
                    <PaymentInput
                        ref={ref}
                        amount={paymentAmount}
                        organizer={organizer?.id}
                        showDoNotChargeCardSwitch={showDoNotChargeCardSwitch}
                        excludePaymentMethods={excludePaymentMethods}
                        isPartnerBooking={isPartnerBooking}
                        onChange={handlePaymentChange}
                        onError={(error) => {
                            helpers.setError(error ?? undefined);
                        }}
                        onAdditionalDetailsSubmit={onAdditionalDetailsSubmit}
                        onAdditionalDetailsSubmitSuccess={onAdditionalDetailsSubmitSuccess}
                    />
                )}

                <CreatePurchaseDepositAndInvoicePaymentFields />
            </div>
        );
    },
);

CreatePurchasePaymentFields.displayName = "CreatePurchasePaymentFields";

const CreatePurchaseDepositAndInvoicePaymentFields = React.memo(() => {
    const hasDeposit = useCreatePurchaseStore((state) => state.hasDeposit());
    const paymentMethod = useCreatePurchaseStore((state) => state.paymentLineItem.payment.method);
    if (!hasDeposit && paymentMethod !== PaymentMethod.INVOICE) {
        return null;
    }

    return (
        <div className="flex flex-col space-y-4 py-4 2xl:w-3/5">
            {hasDeposit && <CreatePurchaseDepositFields />}

            {paymentMethod === PaymentMethod.INVOICE ? <CreatePurchaseInvoiceFields /> : null}
        </div>
    );
});

CreatePurchaseDepositAndInvoicePaymentFields.displayName = "CreatePurchaseDepositAndInvoicePaymentFields";

const depositRemindersTypes = new Set([PaymentReminderType.SELLER_DEPOSIT, PaymentReminderType.BALANCE_DUE_TRAVELER]);
